import * as Y from 'yjs';
import {
    DataGlueGraph,
    NodeInstance,
    DataConnection,
    GlueVariable,
    FunctionDefinition,
    FunctionPortDefinition,
    SingleNodeInstance,
    FunctionNodeInstance,
    DataType,
    UserDefinedType,
    IntegrationAccess,
} from '@glueapp/graph-types';
import {
    META_KEY,
    NODES_KEY,
    CONNECTIONS_KEY,
    VARIABLES_KEY,
    FUNCTIONS_KEY,
    META_SCHEMA_VERSION,
    NODE_LEGACY_INT_ID,
    NODE_NODE_ID,
    NODE_NODE_VERSION,
    NODE_POSITION,
    NODE_POSITION_X,
    NODE_POSITION_Y,
    NODE_WIDTH,
    NODE_HEIGHT,
    NODE_SELECTED_INTEGRATION_ACCESS,
    NODE_SELECTED_VARIABLE,
    NODE_FUNCTION_ID,
    NODE_PORT_VALUES,
    NODE_PORT_USER_DEFINED_TYPES,
    NODE_STATE,
    CONNECTION_LEGACY_INT_ID,
    CONNECTION_SENDING_NODE_ID,
    CONNECTION_SENDING_PORT_NAME,
    CONNECTION_SENDING_PORT_TYPE,
    CONNECTION_RECEIVING_NODE_ID,
    CONNECTION_RECEIVING_PORT_NAME,
    CONNECTION_RECEIVING_PORT_TYPE,
    VARIABLE_TYPE_DETERMINATION,
    VARIABLE_TYPE,
    VARIABLE_RESOLVED_TYPE,
    FUNCTION_NAME,
    FUNCTION_DESCRIPTION,
    FUNCTION_NODES,
    FUNCTION_CONNECTIONS,
    FUNCTION_INPUT_PORT_DEFINITIONS,
    FUNCTION_OUTPUT_PORT_DEFINITIONS,
    FUNCTION_INPUTS_NODE_ID,
    FUNCTION_OUTPUTS_NODE_ID,
    FUNCTION_CREATED_AT,
    FUNCTION_UPDATED_AT,
    FUNCTION_VERSION,
} from './yDocSchema';
import { decodeDataValueMap } from './dataValueCodec';

/**
 * Convert a Y.Doc back into the legacy `DataGlueGraph` shape.
 *
 * Notes:
 *   - The integer `id` on `NodeInstance` and `DataConnection` is the `legacyIntId`
 *     stored at hydration time (or freshly minted from `meta.nextLegacyIntId` for
 *     post-CRDT nodes that haven't been backfilled yet).
 *   - Node arrays are sorted by `legacyIntId` for stable ordering across replicas.
 *   - `selected` is reconstructed as `false` (it lives in awareness state, not the doc).
 *   - `inPorts` / `outPorts` are emitted as empty arrays — they're derived state
 *     populated by `computeShape()` against the node template at render time. Code
 *     paths that need resolved ports (the runner, validation) must layer the
 *     derive step on top of this output.
 */
export function yDocToJson(doc: Y.Doc): DataGlueGraph {
    const meta = doc.getMap(META_KEY);
    const nodesMap = doc.getMap<Y.Map<unknown>>(NODES_KEY);
    const connectionsMap = doc.getMap<Y.Map<unknown>>(CONNECTIONS_KEY);
    const variablesMap = doc.getMap<Y.Map<unknown>>(VARIABLES_KEY);
    const functionsMap = doc.getMap<Y.Map<unknown>>(FUNCTIONS_KEY);

    const nodeUuidToLegacyId = new Map<string, number>();
    for (const [uuid, yNode] of nodesMap.entries()) {
        nodeUuidToLegacyId.set(uuid, yNode.get(NODE_LEGACY_INT_ID) as number);
    }

    const nodeInstances = sortByLegacyId(Array.from(nodesMap.values()).map(yNode => decodeNode(yNode)));
    const connections = sortByLegacyId(
        Array.from(connectionsMap.values())
            .map(yConn => decodeConnection(yConn, nodeUuidToLegacyId))
            .filter((c): c is DataConnection => c !== null),
    );
    const variables = Array.from(variablesMap.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([name, yVar]) => decodeVariable(name, yVar));
    const functions = Array.from(functionsMap.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([id, yFn]) => decodeFunction(id, yFn));

    const result: DataGlueGraph = { nodeInstances, connections, variables, functions };
    const schemaVersion = meta.get(META_SCHEMA_VERSION) as number | undefined;
    if (schemaVersion !== undefined) result.schemaVersion = schemaVersion;
    return result;
}

function decodeNode(yNode: Y.Map<unknown>): NodeInstance {
    const yPosition = yNode.get(NODE_POSITION) as Y.Map<number>;
    const portValuesMap = yNode.get(NODE_PORT_VALUES) as Y.Map<Y.Map<unknown>>;
    const stateMap = yNode.get(NODE_STATE) as Y.Map<Y.Map<unknown>>;
    const portUserDefinedTypesMap = yNode.get(NODE_PORT_USER_DEFINED_TYPES) as Y.Map<unknown>;

    const portUserDefinedTypes: { [key: string]: UserDefinedType } = {};
    for (const [portName, payload] of portUserDefinedTypesMap.entries()) {
        portUserDefinedTypes[portName] = JSON.parse(payload as string) as UserDefinedType;
    }

    const base: SingleNodeInstance = {
        id: yNode.get(NODE_LEGACY_INT_ID) as number,
        nodeId: yNode.get(NODE_NODE_ID) as string,
        position: {
            x: yPosition.get(NODE_POSITION_X) ?? 0,
            y: yPosition.get(NODE_POSITION_Y) ?? 0,
        },
        selected: false,
        portValues: decodeDataValueMap(portValuesMap),
        inPorts: [],
        outPorts: [],
        portUserDefinedTypes,
        state: decodeDataValueMap(stateMap),
    };

    const nodeVersion = yNode.get(NODE_NODE_VERSION) as number | undefined;
    if (nodeVersion !== undefined) base.nodeVersion = nodeVersion;

    const width = yNode.get(NODE_WIDTH) as number | undefined;
    if (width !== undefined) base.width = width;
    const height = yNode.get(NODE_HEIGHT) as number | undefined;
    if (height !== undefined) base.height = height;

    const integrationAccessJson = yNode.get(NODE_SELECTED_INTEGRATION_ACCESS) as string | undefined;
    if (integrationAccessJson !== undefined) {
        base.selectedIntegrationAccess = JSON.parse(integrationAccessJson) as IntegrationAccess;
    }

    const selectedVariable = yNode.get(NODE_SELECTED_VARIABLE) as string | undefined;
    if (selectedVariable !== undefined) base.selectedVariable = selectedVariable;

    const functionId = yNode.get(NODE_FUNCTION_ID) as string | undefined;
    if (functionId !== undefined) {
        return { ...base, functionId } satisfies FunctionNodeInstance;
    }
    return base;
}

function decodeConnection(yConn: Y.Map<unknown>, nodeUuidToLegacyId: Map<string, number>): DataConnection | null {
    const sendingUuid = yConn.get(CONNECTION_SENDING_NODE_ID) as string;
    const receivingUuid = yConn.get(CONNECTION_RECEIVING_NODE_ID) as string;
    const sendingLegacyId = nodeUuidToLegacyId.get(sendingUuid);
    const receivingLegacyId = nodeUuidToLegacyId.get(receivingUuid);
    if (sendingLegacyId === undefined || receivingLegacyId === undefined) {
        // Orphaned connection (referenced node was deleted). Skip; the server
        // reconciler is responsible for actually pruning it from the doc.
        return null;
    }
    return {
        id: yConn.get(CONNECTION_LEGACY_INT_ID) as number,
        sendingNodeInstanceId: sendingLegacyId,
        sendingPortName: yConn.get(CONNECTION_SENDING_PORT_NAME) as string,
        sendingPortType: yConn.get(CONNECTION_SENDING_PORT_TYPE) as DataConnection['sendingPortType'],
        receivingNodeInstanceId: receivingLegacyId,
        receivingPortName: yConn.get(CONNECTION_RECEIVING_PORT_NAME) as string,
        receivingPortType: yConn.get(CONNECTION_RECEIVING_PORT_TYPE) as DataConnection['receivingPortType'],
    };
}

function decodeVariable(name: string, yVar: Y.Map<unknown>): GlueVariable {
    const result: GlueVariable = {
        name,
        typeDetermination: yVar.get(VARIABLE_TYPE_DETERMINATION) as GlueVariable['typeDetermination'],
    };
    const type = yVar.get(VARIABLE_TYPE) as string | undefined;
    if (type !== undefined) result.type = JSON.parse(type) as DataType;
    const resolvedType = yVar.get(VARIABLE_RESOLVED_TYPE) as string | undefined;
    if (resolvedType !== undefined) result.resolvedType = JSON.parse(resolvedType) as DataType;
    return result;
}

function decodeFunction(id: string, yFn: Y.Map<unknown>): FunctionDefinition {
    const fnNodes = yFn.get(FUNCTION_NODES) as Y.Map<Y.Map<unknown>>;
    const fnConnections = yFn.get(FUNCTION_CONNECTIONS) as Y.Map<Y.Map<unknown>>;

    const fnNodeUuidToLegacyId = new Map<string, number>();
    for (const [uuid, yNode] of fnNodes.entries()) {
        fnNodeUuidToLegacyId.set(uuid, yNode.get(NODE_LEGACY_INT_ID) as number);
    }

    const inputsArray = yFn.get(FUNCTION_INPUT_PORT_DEFINITIONS) as Y.Array<Y.Map<unknown>>;
    const outputsArray = yFn.get(FUNCTION_OUTPUT_PORT_DEFINITIONS) as Y.Array<Y.Map<unknown>>;

    const fnInputsUuid = yFn.get(FUNCTION_INPUTS_NODE_ID) as string;
    const fnOutputsUuid = yFn.get(FUNCTION_OUTPUTS_NODE_ID) as string;

    const result: FunctionDefinition = {
        id,
        name: yFn.get(FUNCTION_NAME) as string,
        nodeInstances: sortByLegacyId(Array.from(fnNodes.values()).map(yNode => decodeNode(yNode))),
        connections: sortByLegacyId(
            Array.from(fnConnections.values())
                .map(yConn => decodeConnection(yConn, fnNodeUuidToLegacyId))
                .filter((c): c is DataConnection => c !== null),
        ),
        inputPortDefinitions: inputsArray.toArray().map(decodeFunctionPortDefinition),
        outputPortDefinitions: outputsArray.toArray().map(decodeFunctionPortDefinition),
        functionInputsNodeId: fnNodeUuidToLegacyId.get(fnInputsUuid) ?? 0,
        functionOutputsNodeId: fnNodeUuidToLegacyId.get(fnOutputsUuid) ?? 0,
        createdAt: new Date(yFn.get(FUNCTION_CREATED_AT) as string),
        updatedAt: new Date(yFn.get(FUNCTION_UPDATED_AT) as string),
        version: yFn.get(FUNCTION_VERSION) as number,
    };

    const description = yFn.get(FUNCTION_DESCRIPTION) as string | undefined;
    if (description !== undefined) result.description = description;

    return result;
}

function decodeFunctionPortDefinition(yPort: Y.Map<unknown>): FunctionPortDefinition {
    const result: FunctionPortDefinition = {
        id: yPort.get('id') as string,
        name: yPort.get('name') as string,
        type: JSON.parse(yPort.get('type') as string) as DataType,
    };
    const optional = yPort.get('optional') as boolean | undefined;
    if (optional !== undefined) result.optional = optional;
    const description = yPort.get('description') as string | undefined;
    if (description !== undefined) result.description = description;
    return result;
}

function sortByLegacyId<T extends { id: number }>(items: T[]): T[] {
    return items.sort((a, b) => a.id - b.id);
}
